import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, CurrencyField, Modal, SelectField, TextField, TextareaField } from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import { mensagemDeErro } from "../../../lib/erro-validacao";
import { useDebouncedValue } from "../../../lib/use-debounce";
import { mesAno } from "../../../lib/beneficios";
import { competenciaOk, motivoOk } from "./encerrar-modal";

// INCLUIR UM BENEFÍCIO PELO PORTAL (#320).
//
// Órgão e rubrica vêm do vínculo da consignatária (não das facetas da lista,
// que só têm onde já há benefício). A matrícula é procurada EXATA na foto
// publicada do órgão, pela prévia da API — a mesma leitura que a inclusão faz.
// Antes de confirmar, a prévia diz o que o benefício ocupa na margem, se o
// órgão vai pedir a autorização do servidor e se há arquivo mensal confirmado.
// Ela informa; quem recusa é a inclusão.

export const centavosDe = (reais: number | null) => (reais == null ? 0 : Math.round(reais * 100));

export function IncluirBeneficioModal({ onFechar, onFeito }: { onFechar: () => void; onFeito: (aviso: string) => void }) {
  const qc = useQueryClient();
  const [unidadeId, setUnidadeId] = useState("");
  const [rubrica, setRubrica] = useState("");
  const [matricula, setMatricula] = useState("");
  const [ffEscolhida, setFfEscolhida] = useState("");
  const [valor, setValor] = useState<number | null>(null);
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [motivo, setMotivo] = useState("");
  const [padraoAplicado, setPadraoAplicado] = useState(false);

  const opcoes = useQuery({
    queryKey: ["banco", "beneficios", "opcoes-inclusao"],
    queryFn: () => atlas.banco.beneficios.opcoesInclusao(),
  });
  const orgao = opcoes.data?.orgaos.find((o) => String(o.id) === unidadeId);

  const matriculaDebounced = useDebouncedValue(matricula.trim());
  const valorCentavos = centavosDe(valor);
  const valorDebounced = useDebouncedValue(valorCentavos);
  const inicioValido = competenciaOk(inicio) ? inicio : "";
  const previa = useQuery({
    queryKey: ["banco", "previa-inclusao", unidadeId, rubrica, matriculaDebounced, valorDebounced, inicioValido],
    queryFn: () =>
      atlas.banco.beneficios.previaInclusao({
        unidadeId: Number(unidadeId),
        rubrica,
        matricula: matriculaDebounced || undefined,
        valorCentavos: valorDebounced || undefined,
        competenciaInicio: inicioValido || undefined,
      }),
    enabled: unidadeId !== "" && rubrica !== "" && (inicio === "" || competenciaOk(inicio)),
    placeholderData: keepPreviousData,
    retry: false,
  });
  // A competência nasce com a padrão do órgão, uma vez; trocar de órgão traz a dele.
  useEffect(() => {
    if (padraoAplicado || !previa.data || previa.isPlaceholderData) return;
    setPadraoAplicado(true);
    setInicio(previa.data.competenciaInicioPadrao);
  }, [padraoAplicado, previa.data, previa.isPlaceholderData]);

  // A prévia só vale para o que está na tela agora.
  const p =
    previa.data && !previa.isPlaceholderData && previa.data.competenciaInicio === inicio && matriculaDebounced === matricula.trim()
      ? previa.data
      : null;
  const folhas = p?.servidor?.folhas ?? [];
  const ff = folhas.length === 1 ? folhas[0]! : ffEscolhida;
  const prazo = p?.comportamento === "prazo";
  const fimOk = prazo ? competenciaOk(fim) && fim >= inicio : fim === "" || (competenciaOk(fim) && fim >= inicio);
  const valido =
    p !== null &&
    !p.cortePassou &&
    p.servidor?.podeAverbar === true &&
    ff !== "" &&
    valorCentavos > 0 &&
    valorDebounced === valorCentavos &&
    fimOk &&
    motivoOk(motivo);

  const salvar = useMutation({
    mutationFn: () =>
      atlas.banco.beneficios.incluir({
        unidadeId: Number(unidadeId),
        ff,
        matricula: matricula.trim(),
        rubrica,
        valorCentavos,
        competenciaInicio: inicio,
        ...(fim ? { competenciaFim: fim } : {}),
        motivo: motivo.trim(),
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["banco", "beneficios"] });
      void qc.invalidateQueries({ queryKey: ["banco", "operadora"] });
      const base = r.aguardandoAutorizacao
        ? "Incluído: aguarda a autorização do servidor para começar a descontar."
        : `Incluído: desconta a partir de ${mesAno(inicio)}.`;
      onFeito(r.movimentoDesatualizado ? `${base} O movimento desta competência já foi emitido: a inclusão só vale numa retificação.` : base);
      onFechar();
    },
  });

  return (
    <Modal
      open
      size="lg"
      title="Incluir benefício"
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Incluindo…" : "Incluir"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {opcoes.isError ? <p role="alert">{mensagemDeErro(opcoes.error, "Não foi possível carregar os órgãos.")}</p> : null}
        {opcoes.data && opcoes.data.orgaos.length === 0 ? (
          <p role="status" style={{ margin: 0 }}>
            Nenhum órgão/entidade tem rubrica de benefício vinculada a você. Fale com a averbadora.
          </p>
        ) : null}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <SelectField
            label="Órgão/entidade"
            value={unidadeId}
            onChange={(e) => {
              setUnidadeId(e.target.value);
              setRubrica("");
              setFfEscolhida("");
              setInicio("");
              setPadraoAplicado(false);
            }}
            options={[{ value: "", label: "Escolha…" }, ...(opcoes.data?.orgaos ?? []).map((o) => ({ value: String(o.id), label: o.nome }))]}
          />
          <SelectField
            label="Rubrica"
            value={rubrica}
            disabled={!orgao}
            onChange={(e) => setRubrica(e.target.value)}
            options={[{ value: "", label: "Escolha…" }, ...(orgao?.rubricas ?? []).map((r) => ({ value: r.codigo, label: `${r.codigo} — ${r.rotulo}` }))]}
          />
          <TextField
            label="Matrícula"
            value={matricula}
            onChange={(e) => {
              setMatricula(e.target.value.replace(/\s/g, ""));
              setFfEscolhida("");
            }}
            hint="A matrícula exata, como na folha do órgão."
          />
          {folhas.length > 1 ? (
            <SelectField
              label="Folha (FF)"
              value={ffEscolhida}
              onChange={(e) => setFfEscolhida(e.target.value)}
              options={[{ value: "", label: "Escolha…" }, ...folhas.map((f) => ({ value: f, label: f }))]}
              hint="A matrícula aparece em mais de uma folha."
            />
          ) : null}
          <CurrencyField label="Valor mensal" value={valor} onValueChange={setValor} />
          <TextField
            label="Primeiro desconto em"
            value={inicio}
            placeholder="AAAAMM"
            onChange={(e) => setInicio(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <TextField
            label="Último desconto em"
            value={fim}
            placeholder="AAAAMM"
            onChange={(e) => setFim(e.target.value.replace(/\D/g, "").slice(0, 6))}
            hint={prazo ? "Obrigatório: a rubrica tem prazo." : "Opcional. Vazio = enquanto durar."}
          />
        </div>

        {/* A PRÉVIA: quem é a matrícula e o que a inclusão faz na margem, pela regra do órgão. */}
        <section aria-label="Prévia da inclusão" style={{ padding: 12, borderRadius: 10, background: "var(--bg-elev-2)", display: "grid", gap: 6 }}>
          {!unidadeId || !rubrica ? (
            <span style={{ color: "var(--text-muted)" }}>Escolha o órgão/entidade e a rubrica.</span>
          ) : previa.isError ? (
            <span role="alert">{mensagemDeErro(previa.error, "Não foi possível calcular a prévia.")}</span>
          ) : p ? (
            <>
              {matricula.trim() === "" ? (
                <span style={{ color: "var(--text-muted)" }}>Informe a matrícula.</span>
              ) : p.servidor ? (
                <span>
                  <strong>{p.servidor.nome ?? "Nome não cadastrado"}</strong> · CPF {p.servidor.cpfMasked}
                  {p.servidor.situacao ? ` · ${p.servidor.situacao}` : ""}
                </span>
              ) : (
                <span role="alert">Matrícula não encontrada na foto publicada deste órgão/entidade.</span>
              )}
              {p.servidor && !p.servidor.podeAverbar ? (
                <span role="alert" style={{ color: "var(--danger-500)" }}>{p.servidor.motivo ?? "O servidor não pode receber consignação."}</span>
              ) : null}
              {valorCentavos > 0 ? <strong>{p.mensagem}</strong> : null}
              {p.cortePassou ? (
                <span role="alert" style={{ color: "var(--danger-500)" }}>
                  O corte de {mesAno(p.competenciaInicio)} já passou: escolha um primeiro desconto mais adiante.
                </span>
              ) : null}
              {p.exigeAutorizacao ? (
                <span>O órgão exige a autorização do servidor: o benefício só desconta depois que ele autorizar.</span>
              ) : null}
              {p.arquivoCompletoConfirmado ? (
                <span>
                  Há arquivo mensal confirmado em {mesAno(p.competenciaInicio)}. Enquanto o arquivo vier, ele governa: a linha
                  que ficar fora dele é encerrada.
                </span>
              ) : null}
            </>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>{inicio === "" || competenciaOk(inicio) ? "Calculando…" : "Informe a competência (AAAAMM)."}</span>
          )}
        </section>

        <TextareaField label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} hint="Fica no histórico do benefício." />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível incluir.")}</p>}
      </div>
    </Modal>
  );
}
